import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, ArrowRight, LoaderCircle, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Alert from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import authService from "@/services/auth.service";
import qs from "qs";

const formSchema = yup.object().shape({
  password: yup.string().required("Enter new password").label("Password"),
  confirmPassword: yup
    .string()
    .required("Confirm new password")
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .label("Confirm Password"),
});

export default function ResetPassword() {
  const navigate = useNavigate();
  const form = useForm({
    resolver: yupResolver(formSchema),
    values: {
      password: "",
      confirmPassword: "",
    },
  });

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  const token = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.token;
  const year = new Date().getFullYear();

  const submit = async (values: any) => {
    try {
      setSuccess("");
      setError("");
      await authService.resetPassword({
        password: values.password,
        token: token,
      });
      form.reset();
      setSuccess("Password reset success");
      setError("");
      form.reset();
    } catch (error) {
      console.log(error);
      setError(error.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 lg:fixed lg:inset-y-0 lg:left-0 bg-primary dark:bg-primaryDark relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 dark:opacity-[3%]">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to={"/"}>
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>

          {/* Main Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-card/20 px-4 py-2 rounded-md text-sm">
              <Sparkles className="w-4 h-4" />
              Reset access
            </div>

            <h1 className="text-4xl xl:text-4xl font-semibold leading-tight">
              Set a new password
            </h1>

            <p className="text-base text-white/80 leading-relaxed max-w-md">
              Create a strong new password to securely regain access to your
              workspace.
            </p>

            {/* Feature List */}
            <div className="space-y-4 text-sm">
              {[
                "Use at least 8 characters",
                "Mix letters, numbers, and symbols",
                "Avoid reusing old passwords",
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded-md bg-card/20 flex items-center justify-center">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-white/90">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {["JD", "AK", "MR", "SL"].map((initials, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full bg-card/20 backdrop-blur border-2 border-white/30 flex items-center justify-center text-xs font-medium"
                >
                  {initials}
                </div>
              ))}
            </div>
            <div className="text-sm text-white/80">Security-first access</div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col bg-backgroud lg:ml-[50%]">
        {/* Header */}
        <header className="p-4 sm:p-6 flex justify-between items-center">
          <Link to={"/"}>
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>
          <div className="flex items-center gap-3 w-full justify-end">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground hidden sm:block text-sm">
                Remember your password?
              </span>
              <Button
                onClick={() => navigate("/login")}
                size="sm"
                variant="outline"
                className="border-gray-300"
              >
                Sign in
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </header>

        {/* Form Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-[400px]">
            <Card className="border border-border rounded-md">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center justify-center">
                  <img className="h-11" src="/logo_iconn.png" alt="" />
                </div>
                <CardTitle className="text-lg font-semibold text-center text-foreground">
                  Reset your password
                </CardTitle>
                <CardDescription className="text-center text-muted-foreground">
                  Enter your new password below to change your password.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pb-3">
                <div>
                  {error && <Alert variant="danger">{error}</Alert>}
                  {success && <Alert variant="success">{success}</Alert>}
                  <form onSubmit={form.handleSubmit(submit)}>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label
                          htmlFor="password"
                          className="text-muted-foreground"
                        >
                          Enter new password
                        </Label>
                        <Input
                          placeholder="Enter your password"
                          value={form.watch("password")}
                          onChange={(e) =>
                            form.setValue("password", e.target.value)
                          }
                          type="password"
                          error={form.formState?.errors["password"]?.message}
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label
                          htmlFor="confirmPassword"
                          className="text-muted-foreground"
                        >
                          Confirm new password
                        </Label>
                        <Input
                          placeholder="Confirm your password"
                          value={form.watch("confirmPassword")}
                          onChange={(e) =>
                            form.setValue("confirmPassword", e.target.value)
                          }
                          type="password"
                          error={
                            form.formState?.errors["confirmPassword"]?.message
                          }
                          className="h-11"
                        />
                      </div>
                    </div>
                    <Button
                      disabled={form.formState.isSubmitting}
                      onClick={form.handleSubmit(submit)}
                      className="w-full h-11 bg-primary hover:bg-primaryDark text-white mt-4"
                      type="submit"
                    >
                      {form.formState.isSubmitting && (
                        <LoaderCircle
                          className="-ms-1 me-2 animate-spin"
                          size={16}
                        />
                      )}
                      Reset your password
                    </Button>
                  </form>
                </div>

                <div className="pt-2">
                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-muted-foreground"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to login
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <footer className="p-4 sm:p-6 border-t border-border bg-card">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-muted-foreground">
            <p>© {year} One Suite. All rights reserved.</p>
            <div className="flex gap-4">
              <Link to="#" className="hover:text-muted-foreground">
                Terms
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                Privacy
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                Help
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
